import { useEffect, useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import { format, addDays, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

const getNext10Days = () => {
  return Array.from({ length: 11 }, (_, i) => addDays(new Date(), i));
};

interface DateCarouselProps {
  onDateChange?: (date: string) => void;
  selectedDate?: string; // Add prop to receive current selected date
}

export default function DateCarousel({ onDateChange, selectedDate }: DateCarouselProps) {
  const [viewportRef, embla] = useEmblaCarousel({
    dragFree: false,
    containScroll: 'trimSnaps',
    align: 'start',
    slidesToScroll: 1, // Scroll one day at a time
    loop: false
  });
  const [dates, setDates] = useState<Date[]>([]);
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date>(new Date());
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // Using Tailwind's sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const nextDays = getNext10Days();
    setDates(nextDays);
    
    // Set the selected date based on props or default to today
    if (selectedDate) {
      const parsedDate = parseISO(selectedDate);
      // Validate the parsed date before using it
      if (isValid(parsedDate)) {
        setInternalSelectedDate(parsedDate);
      } else {
        // Fallback to today if parsing fails
        console.warn('Invalid date format received:', selectedDate);
        setInternalSelectedDate(nextDays[0]);
      }
    } else {
      setInternalSelectedDate(nextDays[0]);
    }
  }, [selectedDate]);

  // Update scroll state and sync selected date to the snapped slide (mobile only)
  useEffect(() => {
    if (!embla || !isMobile) return;

    const updateScrollState = () => {
      const canPrev = embla.canScrollPrev();
      const canNext = embla.canScrollNext();
      setCanScrollPrev(canPrev);
      setCanScrollNext(canNext);
    };

    const syncSelectedWithSnap = () => {
      const index = embla.selectedScrollSnap();
      const snappedDate = dates[index];
      if (snappedDate && internalSelectedDate.toDateString() !== snappedDate.toDateString()) {
        console.log('Embla selected snap index:', index, 'Date:', format(snappedDate, 'yyyy-MM-dd'));
        setInternalSelectedDate(snappedDate);
        onDateChange?.(format(snappedDate, 'yyyy-MM-dd'));
      }
    };

    const handleSelect = () => {
      updateScrollState();
      syncSelectedWithSnap();
    };

    embla.on('select', handleSelect);
    embla.on('reInit', handleSelect);
    embla.on('settle', handleSelect);

    // Initial sync
    handleSelect();

    return () => {
      embla.off('select', handleSelect);
      embla.off('reInit', handleSelect);
      embla.off('settle', handleSelect);
    };
  }, [embla, dates, internalSelectedDate, onDateChange, isMobile]);

  const scrollPrev = useCallback(() => {
    if (embla && canScrollPrev && embla.canScrollPrev()) {
      embla.scrollPrev();
    }
  }, [embla, canScrollPrev]);

  const scrollNext = useCallback(() => {
    if (embla && canScrollNext && embla.canScrollNext()) {
      embla.scrollNext();
    }
  }, [embla, canScrollNext]);

  const handleDateClick = (date: Date) => {
    console.log('Date clicked:', format(date, 'yyyy-MM-dd'));
    console.log('onDateChange:', onDateChange);
    setInternalSelectedDate(date);
    if (onDateChange) {
      onDateChange(format(date, 'yyyy-MM-dd'));
    }
  };

  return (
    <div className="w-full bg-sand border-b border-[#E1E3E6] relative">
      <button 
        type="button" 
        className={`absolute top-1/2 -translate-y-1/2 left-2.5 z-10 w-10 h-10 bg-white border border-[#E1E3E6] rounded-full text-[0px] ${
          canScrollPrev 
            ? 'cursor-pointer hover:bg-gray-50' 
            : 'cursor-not-allowed opacity-50'
        }`}
        onClick={scrollPrev}
        disabled={!canScrollPrev}
      >
        <span className="text-base">←</span>
      </button>

      <div className="overflow-hidden" ref={viewportRef}>
        <div className="flex">
          {dates.map((date, index) => {
            const isSelected = internalSelectedDate.toDateString() === date.toDateString();
            return (
              <div 
                key={index} 
                className={`w-full sm:w-[219.8px] shrink-0 flex justify-center items-center py-4 border-r border-[#E1E3E6] text-center ${
                  isSelected ? 'bg-sand font-bold text-holos' : 'hover:bg-sand cursor-pointer'
                }`}
                onClick={() => handleDateClick(date)}
              >
                <div className="text-sm">
                  <p className="m-0">
                    <time dateTime={date.getTime().toString()}>
                      {format(date, 'eeee', { locale: es })}
                    </time>
                    {', '}
                    <time dateTime={date.getTime().toString()}>
                      {format(date, 'dd/MM')}
                    </time>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <button 
        type="button" 
        className={`absolute top-1/2 -translate-y-1/2 right-2.5 z-10 w-10 h-10 bg-white border border-[#E1E3E6] rounded-full text-[0px] ${
          canScrollNext 
            ? 'cursor-pointer hover:bg-gray-50' 
            : 'cursor-not-allowed opacity-50'
        }`}
        onClick={scrollNext}
        disabled={!canScrollNext}
      >
        <span className="text-base">→</span>
      </button>
    </div>
  );
}
